/** Палітра терміналу (референс: екран операцій + нижня панель збірки). */
export const terminalColors = {
  gradientTop: '#FFF9F5',
  gradientBottom: '#FCE4EC',
  cardWhite: '#FFFFFF',
  exitBg: '#F9C87C',
  exitBgHover: '#F0B96A',
  toolbarBg: '#B5B1C5',
  accentOrange: '#FFB347',
  accentOrangeHover: '#F5A623',
  textPrimary: '#1a1a1a',
  infoBadgeBg: '#F0EDE8',
};

export const dashboardGradientSx = {
  minHeight: '100dvh',
  height: '100dvh',
  maxHeight: '100dvh',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
  background: `linear-gradient(180deg, ${terminalColors.gradientTop} 0%, ${terminalColors.gradientBottom} 100%)`,
};

export const operationCardSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  px: 2.5,
  py: 2,
  borderRadius: 2,
  bgcolor: terminalColors.cardWhite,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  cursor: 'pointer',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  '&:active': { transform: 'scale(0.98)' },
  '&:hover': { boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)' },
};

export const documentCardSx = {
  borderRadius: 2,
  bgcolor: terminalColors.cardWhite,
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.07)',
  p: 2,
  mb: 1.5,
  width: '100%',
  boxSizing: 'border-box',
};

export const terminalOrangeButtonSx = {
  bgcolor: terminalColors.accentOrange,
  color: terminalColors.textPrimary,
  borderRadius: 1.5,
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: terminalColors.accentOrangeHover,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: 'rgba(255, 179, 71, 0.45)',
    color: 'rgba(0,0,0,0.4)',
  },
};

export const terminalExitButtonSx = {
  bgcolor: terminalColors.exitBg,
  color: terminalColors.textPrimary,
  borderRadius: 1,
  textTransform: 'none',
  fontWeight: 600,
  px: 2,
  py: 0.75,
  boxShadow: 'none',
  '&:hover': { bgcolor: terminalColors.exitBgHover, boxShadow: 'none' },
};

/** Однакова висота верхньої та нижньої панелі на всіх сторінках терміналу */
export const TERMINAL_BAR_HEIGHT = 44;

export const terminalToolbarSx = {
  bgcolor: terminalColors.toolbarBg,
  color: terminalColors.textPrimary,
  boxShadow: 'none',
};

export const terminalBarToolbarSx = {
  minHeight: TERMINAL_BAR_HEIGHT,
  height: TERMINAL_BAR_HEIGHT,
  py: 0,
  px: 1,
  gap: 0.75,
  flexWrap: 'nowrap',
};

export const terminalBarIconButtonSx = {
  bgcolor: terminalColors.accentOrange,
  color: terminalColors.textPrimary,
  borderRadius: 1.25,
  width: 36,
  height: 36,
  minWidth: 36,
  minHeight: 36,
  flexShrink: 0,
  '&:hover': { bgcolor: terminalColors.accentOrangeHover },
  '&.Mui-disabled': {
    bgcolor: 'rgba(255, 179, 71, 0.45)',
    color: 'rgba(0,0,0,0.4)',
  },
};

export const terminalBottomBarSx = {
  flexShrink: 0,
  bgcolor: terminalColors.toolbarBg,
  px: 1,
  pt: 0,
  minHeight: TERMINAL_BAR_HEIGHT,
  height: `calc(${TERMINAL_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'nowrap',
  gap: 0.75,
  boxSizing: 'border-box',
  overflow: 'hidden',
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
};

/** Outlined Select / TextField select — без синього MUI, акцент помаранчевий */
export const terminalSelectSx = {
  '& .MuiInputLabel-root': {
    color: 'rgba(26, 26, 26, 0.7)',
    '&.Mui-focused': {
      color: terminalColors.accentOrange,
    },
  },
  '& .MuiOutlinedInput-root': {
    bgcolor: terminalColors.cardWhite,
    borderRadius: 1.5,
    color: terminalColors.textPrimary,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.2)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: terminalColors.accentOrange,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: terminalColors.accentOrange,
      borderWidth: 2,
    },
  },
  '& .MuiSelect-select': {
    fontWeight: 500,
  },
  '& .MuiSelect-icon': {
    color: terminalColors.textPrimary,
  },
};

export const terminalSelectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: terminalColors.cardWhite,
      borderRadius: 1.5,
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
      mt: 0.5,
      maxHeight: 280,
      '& .MuiMenuItem-root': {
        color: terminalColors.textPrimary,
        fontWeight: 500,
        '&.Mui-selected': {
          bgcolor: 'rgba(255, 179, 71, 0.28)',
          fontWeight: 600,
          '&:hover': {
            bgcolor: 'rgba(255, 179, 71, 0.38)',
          },
        },
        '&:hover': {
          bgcolor: 'rgba(255, 179, 71, 0.14)',
        },
      },
    },
  },
};

/** Обгортка сторінок терміналу: верхня + контент + нижня панель у видимій області (mobile-safe) */
export const terminalPageShellSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100svh',
  maxHeight: '100dvh',
  width: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box',
};
